/* eslint-disable import/no-restricted-paths */
import { Minus, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { FeedbackToolProvider } from "@/features/request/feedback-tool/feedbackTool/components/FeedbackToolProvider";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { usePdfPinchZoom } from "@/features/request/feedback-tool/feedbackTool/pdf/hooks/usePdfPinchZoom";
import {
  clampZoom,
  usePdfWheelZoom,
} from "@/features/request/feedback-tool/feedbackTool/pdf/hooks/usePdfWheelZoom";
import { useImagePreview } from "@/hooks/useImagePreview";

import type { UseVideoConversionOptions } from "./hooks/useVideoConversion";

import PdfViewer from "../pdf-viewer";
import { useVideoConversion } from "./hooks/useVideoConversion";

const VIDEO_FORMATS = ["avi", "mov", "mkv", "mp4", "m4v"];
const IMAGE_FORMATS = ["png", "bmp", "webp", "jpg", "jpeg", "svg", "gif"];
const PDF_FORMATS = ["pdf"];

const CONTROLS_MAX = 150;
const CONTROLS_MIN = 60;
const MAX_ZOOM = 4;
const MIN_ZOOM = 1;
const BUTTON_ZOOM_FACTOR = 1.25;

const controlsForZoom = (zoom: number) =>
  CONTROLS_MAX -
  ((clampZoom(zoom, MAX_ZOOM) - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) *
    (CONTROLS_MAX - CONTROLS_MIN);

const getMediaType = (url: string) => {
  if (VIDEO_FORMATS.some((format) => url.toLowerCase().includes(format))) {
    return "VIDEO";
  }

  if (IMAGE_FORMATS.some((format) => url.toLowerCase().includes(format))) {
    return "IMAGE";
  }

  if (PDF_FORMATS.some((format) => url.toLowerCase().includes(format))) {
    return "PDF";
  }

  return "UNKNOWN";
};

interface MediaViewerProps {
  alt?: string;
  attachmentId: number;
  className?: string;
  conversionPollOptions?: UseVideoConversionOptions;
  extension: string;
  showZoomControls?: boolean;
  src: string;
  usePreview?: boolean;
}

export function MediaViewer({
  alt = "",
  attachmentId,
  className = "",
  conversionPollOptions,
  extension,
  showZoomControls = true,
  src,
  usePreview = false,
}: MediaViewerProps) {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const controls = controlsForZoom(zoom);

  useEffect(() => {
    setZoom(MIN_ZOOM);
  }, [src]);

  const mediaType = getMediaType(extension);

  const applyZoom = (type: "ZOOM_IN" | "ZOOM_OUT") => {
    setZoom((previous) =>
      clampZoom(
        previous *
          (type === "ZOOM_IN" ? BUTTON_ZOOM_FACTOR : 1 / BUTTON_ZOOM_FACTOR),
        MAX_ZOOM,
      ),
    );
  };

  return (
    <div
      className={`${className} w-full h-full`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Zoom Controls */}
      {showZoomControls && mediaType === "IMAGE" && (
        <div className="absolute bottom-25 right-25 z-20 flex items-center gap-4">
          <div className="flex items-center">
            <Minus
              className="text-white cursor-pointer mr-4 drop-shadow-lg"
              onClick={() => applyZoom("ZOOM_OUT")}
              size={24}
            />
            <div
              className="bg-white border-4 border-[#6938EF] h-6 w-6 rounded-full cursor-pointer absolute transition-all duration-300"
              style={{ right: controls }}
            />
            <div className="bg-white/70 h-3 w-30 rounded-sm" />
            <Plus
              className="text-white cursor-pointer ml-4 mr-4 drop-shadow-lg"
              onClick={() => applyZoom("ZOOM_IN")}
              size={24}
            />
          </div>
        </div>
      )}

      {/* Media Content */}
      <div className="w-full h-full flex justify-center items-center">
        {mediaType === "IMAGE" && (
          <ImageViewer
            alt={alt}
            key={src}
            onZoomChange={setZoom}
            src={src}
            usePreview={usePreview}
            zoom={zoom}
          />
        )}
        {mediaType === "VIDEO" && (
          <VideoViewer
            attachmentId={attachmentId}
            conversionPollOptions={conversionPollOptions}
            extension={extension}
            src={src}
          />
        )}
        {mediaType === "PDF" && (
          <PDFViewer
            attachmentId={attachmentId}
            onZoomChange={setZoom}
            url={src}
            zoom={zoom}
          />
        )}
        {mediaType === "UNKNOWN" && (
          <div className="flex items-center justify-center p-8">
            <p className="text-gray-500">Unsupported media type</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Image Viewer Component
function ImageViewer({
  alt,
  onZoomChange,
  src,
  usePreview,
  zoom,
}: {
  alt: string;
  onZoomChange: (zoom: number) => void;
  src: string;
  usePreview?: boolean;
  zoom: number;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<{ h: number; w: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [canPan, setCanPan] = useState(false);
  const { onError, src: activeSrc } = useImagePreview({
    preferredVariant: usePreview ? "preview" : "original",
    url: src,
  });

  const canPanRef = useRef(canPan);
  const fitRef = useRef(fit);
  const onZoomChangeRef = useRef(onZoomChange);

  canPanRef.current = canPan;
  fitRef.current = fit;
  onZoomChangeRef.current = onZoomChange;

  const [isTouch] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: coarse)").matches,
  );

  const handleLoad = () => {
    const img = imgRef.current;

    if (!img) return;

    img.style.height = "";
    img.style.maxHeight = "";
    img.style.maxWidth = "";
    img.style.width = "";

    const rect = img.getBoundingClientRect();

    setFit({ h: rect.height, w: rect.width });
    onZoomChangeRef.current(MIN_ZOOM);
  };

  useEffect(() => {
    const img = imgRef.current;
    const scroller = scrollerRef.current;

    if (!img || !scroller || !fit) return;

    img.style.height = "auto";
    img.style.maxHeight = "none";
    img.style.maxWidth = "none";
    img.style.width = `${fit.w * zoom}px`;

    setCanPan(
      scroller.scrollWidth > scroller.clientWidth ||
        scroller.scrollHeight > scroller.clientHeight,
    );
  }, [fit, zoom]);

  usePdfWheelZoom({
    baseWidth: fit?.w,
    contentRef: imgRef,
    maxZoom: MAX_ZOOM,
    onZoomChange,
    scrollerRef,
    zoom,
  });

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) return;

    let start: {
      scrollLeft: number;
      scrollTop: number;
      x: number;
      y: number;
    } | null = null;

    const onMouseDown = (event: MouseEvent) => {
      if (event.button !== 0 || !canPanRef.current) return;

      start = {
        scrollLeft: scroller.scrollLeft,
        scrollTop: scroller.scrollTop,
        x: event.clientX,
        y: event.clientY,
      };
      setIsDragging(true);
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!start) return;

      scroller.scrollLeft = start.scrollLeft - (event.clientX - start.x);
      scroller.scrollTop = start.scrollTop - (event.clientY - start.y);
    };

    const onMouseUp = () => {
      if (!start) return;

      start = null;
      setIsDragging(false);
    };

    scroller.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      scroller.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  usePdfPinchZoom({
    contentRef: imgRef,
    enabled: isTouch,
    hScrollerRef: scrollerRef,
    maxWidth: fit ? fit.w * MAX_ZOOM : null,
    minWidth: fit?.w,
    onZoomEnd: (width) => {
      const currentFit = fitRef.current;

      if (!currentFit?.w) return;

      onZoomChangeRef.current(clampZoom(width / currentFit.w, MAX_ZOOM));
    },
    vScrollerRef: scrollerRef,
  });

  const cursor = isDragging
    ? "cursor-grabbing"
    : canPan
      ? "cursor-grab"
      : "cursor-default";

  return (
    <div
      className={`w-full h-full ${isTouch ? "" : "pt-20"}`}
      data-testid="media-viewer-stage"
    >
      <div
        className={`relative flex w-full h-full overflow-auto overscroll-contain touch-pan-x touch-pan-y ${cursor}`}
        data-testid="media-viewer-scroller"
        ref={scrollerRef}
      >
        <img
          alt={alt}
          className="m-auto max-w-full max-h-full object-contain"
          draggable={false}
          onError={onError}
          onLoad={handleLoad}
          ref={imgRef}
          src={activeSrc}
        />
      </div>
    </div>
  );
}

// Video Viewer Component
function VideoViewer({
  attachmentId,
  conversionPollOptions,
  extension,
  src,
}: {
  attachmentId: number;
  conversionPollOptions?: UseVideoConversionOptions;
  extension: string;
  src: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const needsConversion =
    extension.toLowerCase() === "mov" || !/^https?:\/\//i.test(src);
  const { convertedUrl, status } = useVideoConversion(
    attachmentId,
    needsConversion,
    conversionPollOptions,
  );
  const playbackSrc = needsConversion ? (convertedUrl ?? src) : src;

  useEffect(() => {
    videoRef.current?.load();
  }, [playbackSrc]);

  if (needsConversion && status === "converting") {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-500">Preparing preview…</p>
      </div>
    );
  }

  if (needsConversion && status === "unavailable") {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-8">
        <p className="text-gray-500">
          Preview isn&apos;t available for this file.
        </p>
        <a className="text-blue-600 underline" download href={src}>
          Download to view
        </a>
      </div>
    );
  }

  return (
    <div className="relative flex justify-center items-center max-h-full max-w-full">
      <video
        className="max-w-full max-h-full object-contain"
        controls
        ref={videoRef}
        src={playbackSrc}
        style={{ maxHeight: 600 }}
      >
        <source src={playbackSrc} type="video/mp4" />
        Your browser doesn&apos;t support the video tag.
      </video>
    </div>
  );
}

// PDF Viewer Component
function PDFViewer({
  attachmentId,
  onZoomChange,
  url,
  zoom,
}: {
  attachmentId: number;
  onZoomChange: (zoom: number) => void;
  url: string;
  zoom: number;
}) {
  return (
    <FeedbackToolProvider>
      <PDFViewerBody
        attachmentId={attachmentId}
        onZoomChange={onZoomChange}
        url={url}
        zoom={zoom}
      />
    </FeedbackToolProvider>
  );
}

function PDFViewerBody({
  attachmentId,
  onZoomChange,
  url,
  zoom,
}: {
  attachmentId: number;
  onZoomChange: (zoom: number) => void;
  url: string;
  zoom: number;
}) {
  const { isTablet } = useFeedbackTool();

  return (
    <div className={`w-full relative ${isTablet ? "h-full" : "h-full pt-14"}`}>
      <PdfViewer
        attachmentId={attachmentId}
        maxZoom={MAX_ZOOM}
        onZoomChange={onZoomChange}
        url={url}
        zoom={zoom}
      />
    </div>
  );
}
